import { Component, DestroyRef, effect, HostListener, inject, Input, OnInit, signal, WritableSignal, ChangeDetectionStrategy, input } from '@angular/core';
import {
	CostGroup,
	CostGroupCategory,
	CostGroupType,
	CustomerInvoice,
	CustomerInvoiceService,
	GeneralRegulatoryMode,
	Invoice,
	InvoiceAmount,
	InvoiceAmountValue,
	InvoiceCategory,
	InvoiceCategoryType,
	InvoiceDelivery,
	InvoiceDeliveryValue,
	InvoiceService,
} from '@eadsysteme/billingclient-v1';
import { MatButton } from '@angular/material/button';
import { MatDialog, MatDialogActions } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { InvoiceDetailService } from './invoice-detail.service';
import { translate, TranslocoDirective } from '@jsverse/transloco';
import { SessionService } from '../../../../../../services/session.service';
import { SnackbarService } from '../../../../../../services/snackbar.service';
import { MatError, MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatOption, MatSelect, MatSelectTrigger } from '@angular/material/select';
import { MatInput } from '@angular/material/input';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { DateUtil } from '../../../../../../util/DateUtil';
import { DatePipe } from '@angular/common';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle, MatDatepickerToggleIcon } from '@angular/material/datepicker';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { DayjsDateAdapter, MAT_DAYJS_DATE_ADAPTER_OPTIONS, MAT_DAYJS_DATE_FORMATS } from '../../../../../../util/material-dayjs-adapter';
import { PeriodService } from '../../../period.service';
import { VatRateService } from '../../../../../../services/vat-rate.service';
import { DecimalFormatDirective } from '../../../../../../directives/decimal-format.directive';
import { ConfirmationDialogComponent } from '../../../../../confirmation-dialog/confirmation-dialog.component';
import dayjs, { Dayjs } from 'dayjs';

@Component({
	selector: 'vex-invoice',
	providers: [
		DatePipe,
		{
			provide: DateAdapter,
			useClass: DayjsDateAdapter,
			deps: [MAT_DATE_LOCALE, MAT_DAYJS_DATE_ADAPTER_OPTIONS],
		},
		{ provide: MAT_DATE_FORMATS, useValue: MAT_DAYJS_DATE_FORMATS },
	],
	imports: [
		TranslocoDirective,
		FormsModule,
		MatButton,
		MatDialogActions,
		MatIcon,
		MatFormField,
		MatSelect,
		MatLabel,
		MatError,
		MatOption,
		MatInput,
		ReactiveFormsModule,
		MatDatepicker,
		MatDatepickerInput,
		MatDatepickerToggle,
		MatSuffix,
		DecimalFormatDirective,
		MatDatepickerToggleIcon,
		MatSelectTrigger,
	],
	templateUrl: './invoice.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './invoice.component.scss',
})
export class InvoiceComponent implements OnInit {
	protected readonly invoiceDetailService = inject(InvoiceDetailService);
	protected readonly selectedPeriodService = inject(PeriodService);
	protected readonly dateUtil = inject(DateUtil);
	private readonly invoiceService = inject(InvoiceService);
	private readonly customerInvoiceService = inject(CustomerInvoiceService);
	private readonly sessionService = inject(SessionService);
	private readonly snackBar = inject(SnackbarService);
	protected readonly vatRateService = inject(VatRateService);

	private readonly destroyRef: DestroyRef = inject(DestroyRef);
	private readonly dialog = inject(MatDialog);

	readonly hideAll = input<WritableSignal<boolean>>(signal(false));
	@Input() selectedInvoice: Invoice;
	@Input() selectedInvoiceCategory: InvoiceCategory;
	readonly billingId = input<string>(undefined);
	@Input() costGroup: CostGroup;
	readonly periodId = input<string>(undefined);
	readonly costGroupType = input<CostGroupType>(undefined);
	/** When true, hides internal title and uses dialog-aligned action buttons */
	readonly dialogMode = input(false);
	invoiceAmountValues: WritableSignal<InvoiceAmountValue[] | null> = signal([]);
	deliveryValues: WritableSignal<InvoiceDeliveryValue[] | null> = signal([]);
	protected invoiceAmounts: WritableSignal<InvoiceAmount[]> = signal([]);
	protected deliveries: WritableSignal<InvoiceDelivery[]> = signal([]);
	protected serviceDate: WritableSignal<Dayjs> = signal(undefined);
	protected minDate: Dayjs | undefined;
	protected maxDate: Dayjs | undefined;
	protected taxRates: number[] = [];

	protected selectedVatRate: number = this.vatRateService.getDefaultVatRate(GeneralRegulatoryMode.GERMAN_STANDARD);

	protected get currentRegulatoryMode(): GeneralRegulatoryMode {
		return this.selectedPeriodService.selectedPeriod()?.generalRegulatoryMode ?? GeneralRegulatoryMode.GERMAN_STANDARD;
	}

	protected get VAT_RATE_OPTIONS() {
		return this.vatRateService.getVatRateOptions(this.currentRegulatoryMode, this.costGroup.preferredVatRate);
	}

	protected get defaultVatRate(): number {
		return this.vatRateService.getDefaultVatRate(this.currentRegulatoryMode);
	}

	constructor() {
		effect(() => {
			if (this.selectedInvoice) {
				this.selectedInvoice.invoiceDate = this.serviceDate()?.isValid()
					? this.dateUtil.getDateWithoutTimezone(this.serviceDate()).toJSON()
					: undefined;
			}
		});
	}

	ngOnInit(): void {
		if (!this.selectedInvoice) {
			//due to reuse of components - if current invoice is undefined, hide it completely
			this.hideAll().set(true);
			return;
		}

		// Set VAT rate based on custom preferred rate, or costType from costGroup, or fallback to country default
		if (this.costGroup.preferredVatRate != null) {
			this.selectedVatRate = this.costGroup.preferredVatRate;
		} else if (this.costGroup.costType) {
			const typicalRate = this.vatRateService.getTypicalVatRateForCostType(this.costGroup.costType, this.currentRegulatoryMode);
			this.selectedVatRate = typicalRate ?? this.defaultVatRate;
		} else {
			this.selectedVatRate = this.defaultVatRate;
		}

		this.setValues();

		const selectedPeriod = this.selectedPeriodService.selectedPeriod();
		if (selectedPeriod) {
			const validFrom = dayjs(selectedPeriod.validFrom);
			const validTo = dayjs(selectedPeriod.validTo);

			switch (this.selectedInvoiceCategory.type) {
				case InvoiceCategoryType.DELIVERY:
					this.minDate = validFrom;
					this.maxDate = validTo;
					break;
				case InvoiceCategoryType.INITIAL_STOCK:
					this.minDate = DateUtil.minDate;
					this.maxDate = validFrom.add(1, 'day'); // one day beyond the start of the billing period
					break;
				default:
					this.minDate = undefined;
					this.maxDate = undefined;
					break;
			}
		}
	}

	@HostListener('document:keydown.escape', ['$event'])
	onEscapeKey(event: Event) {
		this.selectedInvoice = undefined; //set to undefined, to hide content
		this.invoiceDetailService.showDetailSubject.next(false);
	}

	onKeyUp(event: KeyboardEvent, index: number) {
		if (event.key === 'Delete') {
			this.invoiceAmountValues()[index].vatAmount = null;
			this.onTaxAmountChange(index, null);
		}
	}

	private setValues() {
		this.setAmounts();
		this.setDeliveries();
		this.setAdditionalInfo();
		this.serviceDate.set(this.selectedInvoice?.invoiceDate?.length ? dayjs(this.selectedInvoice.invoiceDate) : undefined);
	}

	private setDeliveries() {
		this.deliveries.set(this.costGroup.invoiceDeliveries);
		if (this.selectedInvoice.invoiceDeliveryValues?.length > 0) {
			this.deliveryValues.set(this.selectedInvoice.invoiceDeliveryValues);
		} else {
			for (const invoiceDelivery of this.costGroup.invoiceDeliveries) {
				this.deliveryValues().push({
					exportId: undefined,
					invoiceDeliveryExportId: invoiceDelivery.exportId,
					amount: undefined,
					unit: invoiceDelivery.physicalUnit,
				});
			}
		}
	}

	private setAmounts() {
		this.invoiceAmounts.set(this.costGroup.invoiceAmounts);
		const amountValues: InvoiceAmountValue[] = [];
		this.taxRates = [];

		for (const invoiceAmount of this.costGroup.invoiceAmounts) {
			const existingValue = this.selectedInvoice.invoiceAmountValues?.find((v) => v.invoiceAmountExportId === invoiceAmount.exportId);

			if (existingValue) {
				amountValues.push({ ...existingValue });
				if (this.isValueSet(existingValue.vatAmount)) {
					if (this.isValueSet(existingValue.netAmount) && existingValue.netAmount !== 0) {
						this.taxRates.push(Number(((existingValue.vatAmount / existingValue.netAmount) * 100).toFixed(2)));
					} else if (this.isValueSet(existingValue.grossAmount)) {
						this.taxRates.push(Number(((existingValue.vatAmount / (existingValue.grossAmount - existingValue.vatAmount)) * 100).toFixed(2)));
					} else {
						this.taxRates.push(this.selectedVatRate);
					}
				} else {
					this.taxRates.push(null);
				}
			} else {
				amountValues.push({
					exportId: undefined,
					invoiceAmountExportId: invoiceAmount.exportId,
					grossAmount: null,
					vatAmount: null,
					netAmount: null,
				});
				this.taxRates.push(this.selectedVatRate);
			}
		}
		this.invoiceAmountValues.set(amountValues);
	}

	private setAdditionalInfo() {
		// Nur bei neuen Rechnungen (ohne ID) die Defaultwerte setzen
		if (!this.selectedInvoice.exportId) {
			// pre-set date only for category HEAT_ENERGY
			const costGroupType = this.costGroupType();
			if (costGroupType.category == CostGroupCategory.HEAT_ENERGY) {
				this.serviceDate.set(dayjs(this.selectedPeriodService.selectedPeriod().validTo));
			}

			if (this.selectedInvoiceCategory.type === 'INITIAL_STOCK') {
				// pre-set date only for category HEAT_ENERGY
				if (costGroupType.category == CostGroupCategory.HEAT_ENERGY) {
					this.serviceDate.set(dayjs(this.selectedPeriodService.selectedPeriod().validFrom));
				}
			}
		}
	}

	delete() {
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			disableClose: false,
			autoFocus: false,
			restoreFocus: false,
		});
		dialogRef.componentInstance.titleMessage = translate('errors.warning');
		dialogRef.componentInstance.confirmMessage = translate('billing.confirmDeleteInvoice', { name: this.selectedInvoice.name });

		dialogRef.afterClosed().subscribe((result) => {
			if (result) {
				this.invoiceService
					.deleteInvoice(this.sessionService.effectiveArea, this.billingId(), this.selectedInvoice.exportId)
					.pipe(takeUntilDestroyed(this.destroyRef))
					.subscribe({
						next: () => {
							this.snackBar.success(translate('billing.deleteInvoiceSuccess'));
							this.invoiceDetailService.showDetailSubject.next(false);
							this.selectedInvoice = undefined; //set to undefined, to hide content
						},
						error: (error) => {
							this.snackBar.error(translate('billing.deleteInvoiceError' + error.message));
						},
					});
			}
		});
	}

	save() {
		const amountValuesToSave = [];
		for (const amountValue of this.invoiceAmountValues().values()) {
			if (!this.showNet()) {
				amountValue.netAmount = null;
			}
			if (!this.showVat()) {
				amountValue.vatAmount = null;
			}
			if (!this.showGross()) {
				amountValue.grossAmount = null;
			}
			if (this.isValueSet(amountValue?.netAmount) || this.isValueSet(amountValue?.grossAmount)) {
				amountValuesToSave.push(amountValue); //only save values, if value given - else it will be deleted in the backend
			}
		}

		const deliveryValuesToSave = [];
		for (const deliveryValue of this.deliveryValues().values()) {
			if (this.isValueSet(deliveryValue?.amount) && this.isValueSet(deliveryValue?.unit)) {
				deliveryValuesToSave.push(deliveryValue);
			}
		}

		// TODO(billingclient-v637): Invoice-Create/Update wandert in CustomerInvoiceService bzw. ServiceCompanyInvoiceService.
		// Hier: Default auf CustomerInvoiceService bis Subtyp-Routing geklärt.
		const invoiceToSave: CustomerInvoice = {
			exportId: this.selectedInvoice.exportId ?? undefined,
			name: this.selectedInvoice.name,
			invoiceDate: this.serviceDate()?.isValid() ? this.dateUtil.getDateWithoutTimezone(this.serviceDate()).toJSON() : undefined,
			invoiceAmountValues: amountValuesToSave,
			invoiceDeliveryValues: deliveryValuesToSave,
		};
		let createOrUpdate: Observable<CustomerInvoice> = this.customerInvoiceService.createCustomerInvoice(
			this.sessionService.effectiveArea,
			this.billingId(),
			this.selectedInvoiceCategory.exportId,
			invoiceToSave
		);
		if (invoiceToSave?.exportId) {
			createOrUpdate = this.customerInvoiceService.updateCustomerInvoice(
				this.sessionService.effectiveArea,
				this.billingId(),
				this.selectedInvoice.exportId,
				invoiceToSave
			);
		}
		createOrUpdate.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
			next: (savedInvoice) => {
				this.snackBar.success(translate('billing.savedInvoiceSuccess') + ' ' + savedInvoice.name);
				this.selectedInvoice = undefined; //set to undefined, to hide content
				this.invoiceDetailService.showDetailSubject.next(false);
			},
			error: (error) => {
				this.snackBar.error(translate('billing.savedInvoiceError'));
			},
		});
	}

	cancel() {
		this.selectedInvoice = undefined; //set to undefined, to hide content
		this.invoiceDetailService.showDetailSubject.next(false);
	}

	isInvoiceValid(): boolean {
		if (!this.selectedInvoice.name?.trim()) {
			return false;
		}

		if (this.serviceDate()) {
			if (!this.serviceDate().isValid()) {
				return false;
			}

			if ((this.minDate && this.serviceDate().isBefore(this.minDate, 'day')) || (this.maxDate && this.serviceDate().isAfter(this.maxDate, 'day'))) {
				return false;
			}
		} else {
			return false;
		}

		// uncommented - negative values should be allowed..
		// if (this.taxRates.some(rate => rate !== null && rate < 0)) {
		//     return false;
		// }

		const hasAllDeliveryValues = this.deliveryValues().every((v) => v.amount !== null && v.amount !== undefined && v.unit);

		if (!hasAllDeliveryValues) {
			return false;
		}

		if (this.selectedInvoiceCategory.type === 'REMAINING_STOCK') {
			return true;
		}

		return this.hasRequiredAmountValues();
	}

	private hasRequiredAmountValues(): boolean {
		const vatType = this.selectedPeriodService.selectedPeriod().vatType;
		const requiredFields = this.getRequiredAmountFields(vatType);
		return this.invoiceAmountValues().every((amountValue) =>
			requiredFields.every((field) => this.isValueSet(amountValue[field] as number | string | null | undefined))
		);
	}

	private getRequiredAmountFields(vatType: string | undefined): Array<keyof InvoiceAmountValue> {
		switch (vatType) {
			case 'GROSS':
				return ['grossAmount'];

			case 'NET':
				return ['netAmount'];

			case 'GROSS_VAT':
				// return ['grossAmount', 'vatAmount'];
				return ['grossAmount'];

			case 'NET_VAT':
				// return ['netAmount', 'vatAmount'];
				return ['netAmount'];

			case 'GROSS_NET_VAT':
				// return ['grossAmount', 'netAmount', 'vatAmount'];
				return ['grossAmount', 'netAmount'];

			case undefined:
			default:
				// Wenn kein vatType gesetzt ist, sind alle Felder optional
				// Aber mindestens eines sollte gesetzt sein
				// return ['grossAmount', 'netAmount', 'vatAmount'];
				return [];
		}
	}

	protected isValueSet(value: number | string | null | undefined): boolean {
		return value !== null && value !== undefined && (typeof value !== 'number' || !Number.isNaN(value));
	}

	private getValidationErrors(): string[] {
		const errors: string[] = [];

		// Delivery Values Check
		const missingDeliveries = this.deliveryValues().filter((v) => !v.amount || !v.unit);
		if (missingDeliveries.length > 0) {
			errors.push(`${missingDeliveries.length} Lieferwert(e) unvollständig`);
		}

		// Amount Values Check (außer bei REMAINING_STOCK)
		if (this.selectedInvoiceCategory.type !== 'REMAINING_STOCK') {
			if (!this.hasRequiredAmountValues()) {
				errors.push('Erforderliche Beträge fehlen');
			}
		}

		return errors;
	}

	private areDeliveryValuesComplete(): boolean {
		return this.deliveryValues().every((v) => this.isValueSet(v.amount) && v.unit);
	}

	showGross(): boolean {
		return (
			this.selectedPeriodService.selectedPeriod().vatType === undefined ||
			this.selectedPeriodService.selectedPeriod().vatType === 'GROSS' ||
			this.selectedPeriodService.selectedPeriod().vatType === 'GROSS_VAT' ||
			this.selectedPeriodService.selectedPeriod().vatType === 'GROSS_NET_VAT'
		);
	}

	showNet() {
		return (
			this.selectedPeriodService.selectedPeriod().vatType === undefined ||
			this.selectedPeriodService.selectedPeriod().vatType === 'NET' ||
			this.selectedPeriodService.selectedPeriod().vatType === 'NET_VAT' ||
			this.selectedPeriodService.selectedPeriod().vatType === 'GROSS_NET_VAT'
		);
	}

	showVat() {
		return (
			(this.selectedPeriodService.selectedPeriod().vatType === undefined ||
				this.selectedPeriodService.selectedPeriod().vatType === 'GROSS_VAT' ||
				this.selectedPeriodService.selectedPeriod().vatType === 'NET_VAT' ||
				this.selectedPeriodService.selectedPeriod().vatType === 'GROSS_NET_VAT') &&
			this.selectedInvoiceCategory.type !== 'REMAINING_STOCK'
		);
	}

	onGrossAmountChange(index: number, value: number) {
		const values = this.invoiceAmountValues();
		if (!values) return;

		const item = values[index];
		if (!this.isValueSet(item.netAmount) || !this.showNet()) {
			item.netAmount = value / (1 + this.taxRates[index] / 100);
		}
		if (this.isValueSet(item.grossAmount)) {
			item.vatAmount = ((value - item.netAmount) * 100) / 100;
			this.taxRates[index] = Number(((item.vatAmount / item.netAmount) * 100).toFixed(2));
		} else {
			item.vatAmount = null;
		}

		this.invoiceAmountValues.set([...values]);
	}

	onNetAmountChange(index: number, value: number) {
		const values = this.invoiceAmountValues();
		if (!values) return;

		const item = values[index];
		item.vatAmount = value ? Math.round(value * this.taxRates[index]) / 100 : 0;
		item.grossAmount = ((value + item.vatAmount) * 100) / 100;

		this.invoiceAmountValues.set([...values]);
	}

	protected onTaxAmountChange(index: number, value: number) {
		const values = this.invoiceAmountValues();
		if (!values) return;

		const item = values[index];

		if (!this.isValueSet(item.grossAmount)) {
			if (this.isValueSet(item.netAmount)) {
				item.grossAmount = ((item.netAmount + value) * 100) / 100;
			} else {
				item.grossAmount = value; // nothing present - set gross = tax
			}
		}
		item.netAmount = item.grossAmount - value;
		this.taxRates[index] = value === null ? null : Number(((value / item.netAmount) * 100).toFixed(2));

		this.invoiceAmountValues.set([...values]);
	}

	protected onVatRateSelect(rate: number) {
		this.selectedVatRate = rate;
		const values = this.invoiceAmountValues();
		if (!values) return;

		for (let index = 0; index < values.length; index++) {
			this.taxRates[index] = rate;
			const item = values[index];

			if (this.isValueSet(item.grossAmount)) {
				// Calculate net and VAT from gross
				item.netAmount = item.grossAmount / (1 + rate / 100);
				item.vatAmount = rate === 0 ? 0 : ((item.grossAmount - item.netAmount) * 100) / 100;
			} else if (this.isValueSet(item.netAmount)) {
				// Calculate VAT and gross from net
				item.vatAmount = rate === 0 ? 0 : Math.round(item.netAmount * rate) / 100;
				item.grossAmount = ((item.netAmount + item.vatAmount) * 100) / 100;
			}
		}

		this.invoiceAmountValues.set([...values]);
	}

	protected readonly InvoiceCategoryType = InvoiceCategoryType;
}

export default InvoiceComponent;
